import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { StringFormatter } from '@repo/toolkit/string'
import { toFullName } from '@repo/toolkit/to-full-name'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { getCenterTefls } from '../../../../api/centers/center-tefl/get-center-tefls'
import useTranslate from '../../../../lib/hooks/use-translate'
import { AssignTeflToCenterGroupFormInput } from '../../../../lib/types/models/center-group/center-group-input'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedOption } from '../../../../lib/utils/functions/misc.functions'
import { createNumberSchema } from '../../../../lib/utils/validations/int-array-validation'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledSelectInput from '../../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../../elements/validation-buttons'

type Props = BaseFormProps<AssignTeflToCenterGroupFormInput> & {
  className?: string
  centerId: number
}

const formSchema: ZodType<AssignTeflToCenterGroupFormInput> = z.object({
  teflId: createNumberSchema('TEFL', { nullish: true })
})

const AssignTeflToCenterGroupForm = ({
  className,
  centerId,
  defaultValues,
  ...validationButtonProps
}: Props) => {
  const { t } = useTranslate()
  const form = useForm<AssignTeflToCenterGroupFormInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const { data: centerTefls } = useQuery({
    queryKey: ['center-tefls', centerId],
    queryFn: () => getCenterTefls(centerId),
    enabled: !!centerId
  })

  const teflOptions =
    centerTefls?.data
      .filter((ct) => !!ct.tefl)
      .map((ct) => ({
        label: toFullName(ct.tefl?.firstName, ct.tefl?.lastName),
        value: ct.tefl!.id
      })) ?? []

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={cn('flex flex-col gap-4', className)}>
          <FormField
            control={form.control}
            name="teflId"
            render={({ field }) => {
              const selectedTefl = centerTefls?.data.find(
                (ct) => ct.tefl?.id === field.value
              )?.tefl
              return (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledSelectInput
                      {...field}
                      optional
                      optionalText={t('center.groups.assign.tefl.none')}
                      label={t('center.groups.assign.tefl.select')}
                      options={teflOptions}
                      selectedOption={
                        field.value === null
                          ? {
                              label: t('center.groups.assign.tefl.none'),
                              value: null as unknown as number
                            }
                          : getSelectedOption(
                              field.value,
                              selectedTefl
                                ? `${StringFormatter.capitalize(selectedTefl.firstName)} ${selectedTefl.lastName.toUpperCase()}`
                                : ''
                            )
                      }
                    />
                  </FormControl>
                </FormItem>
              )
            }}
          />
        </div>

        <ValidationButtons {...validationButtonProps} hasBorderTop />
      </form>
    </Form>
  )
}

export default AssignTeflToCenterGroupForm
